import { motion } from "framer-motion";
import { ShieldCheck, Lock, Eye, BadgeCheck, Radio, Ban } from "lucide-react";
import { CONTACT_EMAIL } from "@/constants/site";

const pillars = [
  { icon: BadgeCheck, t: "Verify to go live", d: "Creators verify their identity via Stripe Identity before broadcasting — real, accountable people." },
  { icon: Eye, t: "Location coarsening", d: "When you go offline, your position blurs to a city-level area so you never leak an exact address." },
  { icon: ShieldCheck, t: "AI moderation", d: "Real-time AI text moderation plus report & block tools. Explicit content is never allowed." },
  { icon: Lock, t: "Privacy by design", d: "Privacy-hardened DMs, minimal data collection, and full control over your information." },
];

export default function Safety() {
  return (
    <div data-testid="safety-page">
      <section className="px-6 pb-12 pt-32 md:px-12 md:pt-44">
        <div className="mx-auto max-w-4xl text-center">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-bold uppercase tracking-[0.2em] text-aura-pink">
            Community Guidelines & Security
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mt-4 font-heading text-4xl font-semibold tracking-tighter text-aura-ink sm:text-6xl">
            A safe place to go live.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-aura-text2">
            Live broadcasting is powerful — and it carries responsibility. These
            guidelines keep AuraLive a trusted, respectful community for everyone.
            By using AuraLive you agree to follow them.
          </motion.p>
        </div>
      </section>

      <section className="px-6 py-8 md:px-12">
        <div className="mx-auto grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p) => (
            <div key={p.t} className="rounded-3xl border border-black/[0.06] bg-white p-7 solid-card">
              <span className="grid h-11 w-11 place-items-center rounded-2xl grad-bg text-white">
                <p.icon size={20} />
              </span>
              <h3 className="mt-5 font-heading text-lg font-medium tracking-tight text-aura-ink">{p.t}</h3>
              <p className="mt-2 text-sm leading-6 text-aura-text2">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-24 pt-12 md:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="legal-body">
            <h2 className="flex items-center gap-3">
              <Radio size={22} className="text-aura-pink" /> Broadcasting rules — read before going live
            </h2>
            <p>You represent, warrant and agree that:</p>
            <ul>
              <li>You will not broadcast minors as recognizable subjects.</li>
              <li>You have the necessary consent from any people whose faces or voices are identifiable in your broadcast. In many places, recording identifiable people without consent may be illegal.</li>
              <li>You will not broadcast from private property you do not own, occupy or have permission to enter, or from sensitive locations such as schools, hospitals, courtrooms, military bases or security-sensitive facilities.</li>
              <li>You will not broadcast criminal activity, incitement to violence, self-harm content or anything that endangers you, your subjects or your viewers.</li>
              <li>You will never broadcast sexually explicit content, nudity or content that sexualizes minors.</li>
              <li>You will not use AuraLive to stalk, harass, dox or harm any person.</li>
              <li>You will comply with all local recording, privacy, broadcasting and public-decency laws where you broadcast.</li>
            </ul>
            <p>Violation may result in immediate account termination, forfeiture of accumulated coins and reporting to law enforcement where required.</p>

            <h2 className="flex items-center gap-3">
              <Ban size={22} className="text-aura-pink" /> Content that is never allowed
            </h2>
            <ul>
              <li>Illegal content or content that violates third-party intellectual property rights.</li>
              <li>Abuse, threats, harassment, defamation or hate speech.</li>
              <li>Discrimination or incitement to violence or illegal activity.</li>
              <li>Spam, phishing or malware.</li>
              <li>Content that violates the privacy or personal-data rights of others.</li>
              <li>Explicit violence or sexual content.</li>
            </ul>
            <p>
              We may remove content, suspend or terminate repeat violators, and
              cooperate with law enforcement where illegal content is reported. To
              report content, contact us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or use in-app
              report, block and mute tools.
            </p>

            <h2 id="security" className="flex items-center gap-3 scroll-mt-28">
              <Lock size={22} className="text-aura-pink" /> Security
            </h2>
            <p>
              We care about safeguarding the confidentiality of your information
              and provide physical, electronic and procedural safeguards to
              protect the data we process and maintain.
            </p>
            <ul>
              <li><strong>Encryption in transit:</strong> traffic between the app and our services is encrypted.</li>
              <li><strong>Atomic transactions:</strong> coin, gift and tip movements are processed as single, safe operations to prevent partial or duplicate transfers.</li>
              <li><strong>Access controls:</strong> access to personal data is limited to what is necessary to operate the service, with a role-based admin model and audit logging.</li>
              <li><strong>Breach notification:</strong> if a data breach affects your personal data, we will notify you in accordance with applicable law.</li>
            </ul>
            <p>
              No method of transmission or storage is 100% secure, so we also rely
              on you to keep your device and account credentials safe. We strongly
              advise against jailbreaking or rooting your device.
            </p>

            <h2>Reporting & appeals</h2>
            <p>
              If you believe content violates these guidelines, infringes your
              rights or is unlawful, report it to{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Users
              affected by a moderation decision may contact us to request further
              review, and we will respond within a reasonable period with the
              reasons for any upheld decision, subject to applicable law.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
